import { CustomerPaymentMethodsCard } from '@folio/components';
import { useAccessiblePayerCustomerIds } from '@folio/hooks/useAccessiblePayerCustomerIds';
import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';

import { loadStripe } from '@stripe/stripe-js';
import { useSuspenseQueries, useSuspenseQuery } from '@tanstack/react-query';
import {
  configControllerGetPublicProductConfigQueryOptions,
  customerControllerGetCustomerQueryOptions,
} from 'korint-sdk';
import { useKorintModuleConfig } from 'korint-sdk/react';
import { Fragment, useMemo } from 'react';
import { usePolicyOnDashboard } from '../../utils/usePolicyOnDashboard';

export function PaymentMethodModule({ addButtonLabel }: { addButtonLabel?: string } = {}) {
  const { policy, workingBranch, branchIdForPolicy } = usePolicyOnDashboard();
  const methodPayment = useKorintModuleConfig('methodPayment');
  const customerInformations = usePolicyProductConfig('customerInformations');

  const payerCustomerIds = useAccessiblePayerCustomerIds(policy);

  const { data: paymentMethodTypes } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: data => ({
      allowedDefault: data.payment?.paymentMethodOnRecurringPayment ?? [],
      // A configured fallback type is chargeable too, so removing it can still leave the
      // payer unbillable — the backend's delete guard counts both.
      chargeable: [
        ...(data.payment?.paymentMethodOnRecurringPayment ?? []),
        ...(data.payment?.paymentMethodOnBrokenMethod ?? []),
      ],
    }),
  });

  const { data: allowedPaymentModes } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    select: data => data.payment?.allowedPaymentModes ?? [],
  });

  const stripePromise = useMemo(() => {
    return loadStripe(methodPayment.stripePublishableKey);
  }, [methodPayment.stripePublishableKey]);

  const customers = useSuspenseQueries({
    queries: payerCustomerIds.map(customerId => ({
      // branchIdForPolicy, not workingBranch?.id: the branch list can lag after a fork, and the
      // funnel ValidationFooter keys its queries on the raw URL branchId — both must stay equal.
      ...customerControllerGetCustomerQueryOptions(customerId, {
        branchId: branchIdForPolicy,
      }),
    })),
  });

  const hasMultipleCustomers = payerCustomerIds.length > 1;

  return (
    <>
      {payerCustomerIds.map((customerId, index) => {
        const customer = customers[index]?.data;
        const customerLabel = hasMultipleCustomers
          ? customerDisplayName(customer ? [customer] : [], customerInformations?.fieldsConfig) ||
            customerId
          : undefined;

        return (
          <Fragment key={customerId}>
            <CustomerPaymentMethodsCard
              customerId={customerId}
              stripePromise={stripePromise}
              allowMandateSignature={!workingBranch}
              addButtonLabel={addButtonLabel}
              // The backend only stores methods when the product supports automatic collection —
              // on an offline-only product the add button could never succeed.
              canAddPaymentMethod={
                policy.status !== 'POLICY_CREATED' && allowedPaymentModes.includes('DIRECT_DEBIT')
              }
              allowedDefaultPaymentMethodTypes={paymentMethodTypes.allowedDefault}
              chargeablePaymentMethodTypes={paymentMethodTypes.chargeable}
              customerLabel={customerLabel}
              productId={policy.productId}
              paymentMode={customer?.paymentMode}
              // The backend refuses OFFLINE on a multi-payer policy, so the off-platform
              // pseudo-method only shows when it could be selected.
              offlineEnabled={allowedPaymentModes.includes('OFFLINE') && !hasMultipleCustomers}
              onPaymentModeUpdated={() => customers[index]?.refetch()}
            />
          </Fragment>
        );
      })}
    </>
  );
}
